import React, { useState, useEffect } from 'react';
import { useI18n } from '../i18n';

const ContactTab = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+52',
    phone: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Animation effect on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.contact-hero, .contact-section, .contact-info-grid');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // (No longer needed: we will display full names inside dropdown options,
  // and show only the numeric code in the collapsed view using an overlay.)

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Combine country code and phone into a single phone string for submission
    const payload = {
      'form-name': 'contact',
      ...formData,
      phone: `${formData.countryCode || ''} ${formData.phone || ''}`.trim()
    };
    // Submit via Netlify Forms (URL-encoded POST to '/')
    const encoded = new URLSearchParams(payload).toString();
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encoded
    })
      .then(() => {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          countryCode: '+52',
          phone: '',
          message: ''
        });
        // Hide success message after a few seconds
        setTimeout(() => setIsSubmitted(false), 4000);
      })
      .catch((err) => setError(err.message || 'Failed to send message'))
      .finally(() => setIsSubmitting(false));
  };

  const imageUrl = (fileName) => `${process.env.PUBLIC_URL}/Images/${encodeURIComponent(fileName)}`;

  const { t } = useI18n();
  return (
    <div className="contact-page">
      {/* Hero Section */}
      <div className="contact-hero" style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url('${imageUrl('FOTO DRONE 10.JPG')}')`
      }}>
        <div className="contact-hero-content">
          <h1 className="contact-hero-title">{t('contact_title')}</h1>
          <p className="contact-hero-subtitle">{t('contact_subtitle')}</p>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="contact-section">
        <div className="contact-section-content">
          <div className="contact-form-container">
            <div className="contact-form-header">
              <h2>{t('contact_form_title')}</h2>
              <p>{t('contact_form_desc')}</p>
            </div>
            
            {isSubmitted ? (
              <div className="form-success">
                <div className="success-icon">✓</div>
                <h3>{t('contact_success_title')}</h3>
                <p>{t('contact_success_desc')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form" name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field">
                <input type="hidden" name="form-name" value="contact" />
                <input type="hidden" name="bot-field" onChange={handleInputChange} />
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('contact_name_label')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('contact_name_placeholder')}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">{t('contact_email_label')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('contact_email_placeholder')}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">{t('contact_phone_label')}</label>
                    <div className="phone-field">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        aria-label="Country code"
                        className="country-code-select"
                      >
                        <option value="+52" aria-label="Mexico +52" title="Mexico">Mexico +52</option>
                        <option value="+1" aria-label="United States / Canada +1" title="United States / Canada">United States / Canada +1</option>
                        <option value="+44" aria-label="United Kingdom +44" title="United Kingdom">United Kingdom +44</option>
                        <option value="+34" aria-label="Spain +34" title="Spain">Spain +34</option>
                        <option value="+49" aria-label="Germany +49" title="Germany">Germany +49</option>
                        <option value="+33" aria-label="France +33" title="France">France +33</option>
                        <option value="+61" aria-label="Australia +61" title="Australia">Australia +61</option>
                        <option value="+55" aria-label="Brazil +55" title="Brazil">Brazil +55</option>
                        <option value="+91" aria-label="India +91" title="India">India +91</option>
                        <option value="+81" aria-label="Japan +81" title="Japan">Japan +81</option>
                        <option value="+86" aria-label="China +86" title="China">China +86</option>
                        <option value="+46" aria-label="Sweden +46" title="Sweden">Sweden +46</option>
                        <option value="+47" aria-label="Norway +47" title="Norway">Norway +47</option>
                        <option value="+45" aria-label="Denmark +45" title="Denmark">Denmark +45</option>
                        <option value="+31" aria-label="Netherlands +31" title="Netherlands">Netherlands +31</option>
                        <option value="+39" aria-label="Italy +39" title="Italy">Italy +39</option>
                        <option value="+41" aria-label="Switzerland +41" title="Switzerland">Switzerland +41</option>
                        <option value="+32" aria-label="Belgium +32" title="Belgium">Belgium +32</option>
                        <option value="+43" aria-label="Austria +43" title="Austria">Austria +43</option>
                        <option value="+64" aria-label="New Zealand +64" title="New Zealand">New Zealand +64</option>
                        <option value="+27" aria-label="South Africa +27" title="South Africa">South Africa +27</option>
                        <option value="+351" aria-label="Portugal +351" title="Portugal">Portugal +351</option>
                        <option value="+353" aria-label="Ireland +353" title="Ireland">Ireland +353</option>
                        <option value="+420" aria-label="Czech Republic +420" title="Czech Republic">Czech Republic +420</option>
                        <option value="+7" aria-label="Russia +7" title="Russia">Russia +7</option>
                      </select>
                      <span className="country-code-display" aria-hidden="true">{formData.countryCode}</span>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder={t('contact_phone_placeholder')}
                        className="phone-input"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">{t('contact_message_label')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="6"
                    placeholder={t('contact_message_placeholder')}
                    required
                  ></textarea>
                </div>
                {error && (
                  <p className="form-error" role="alert" style={{ color: '#c00', marginTop: '0.5rem' }}>
                    {error}
                  </p>
                )}

                <button type="submit" className="contact-submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? t('contact_sending') : t('contact_send')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactTab;
